import { useEffect, useState } from 'react';
import { Modal, Grid, Paper, Typography, styled, Box, Button, CircularProgress, Alert } from '@mui/material';
import MovieIcon from '@mui/icons-material/Movie';
import agent from '../api/agent';
import { VideoGalleryItem } from '../models/videoGalleryItem';
import { env } from '../../app/utils/env';

interface VideoPickerProps {
  open: boolean;
  onClose: () => void;
  onSelect: (url: string) => void;
}

const Item = styled(Paper)(({ theme }) => ({
  backgroundColor: "#f6f6f6",
  padding: theme.spacing(2),
  textAlign: "center",
  color: theme.palette.text.secondary,
  height: "100%",
  cursor: "pointer",
  border: "2px solid transparent",
  transition: "border-color 0.2s ease, transform 0.2s ease",
  '&:hover': {
    borderColor: "#1976d2",
    transform: "translateY(-2px)",
  },
}));

export default function VideoPickerModal({ open, onClose, onSelect }: VideoPickerProps) {
  const [videos, setVideos] = useState<VideoGalleryItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      void fetchVideos();
    }
  }, [open]);

  const fetchVideos = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await agent.VideoGalleryItems.list();
      setVideos(response);
    } catch {
      setVideos([]);
      setError("Video galerisi yüklenemedi.");
    } finally {
      setIsLoading(false);
    }
  };

  const resolveAssetUrl = (value?: string | null) =>
    value ? (value.startsWith('http') ? value : env + value) : '';

  const handleSelect = (videoUrl?: string | null) => {
    if (!videoUrl) return;
    onSelect(videoUrl);
    onClose();
  };

  const getDisplayName = (video: VideoGalleryItem) => {
    if (video.fileName) return video.fileName;
    const clean = (video.videoUrl || '').split(/[?#]/)[0];
    const parts = clean.split("/");
    return parts.pop() || "video.mp4";
  };

  return (
    <Modal open={open} onClose={onClose} className="modal gallery-modal">
      <div className="modal-content">
        {isLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : videos.length === 0 ? (
          <Alert severity="info">Henüz yüklenmiş video bulunmuyor.</Alert>
        ) : (
          <Grid container spacing={2}>
            {videos.map((video) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={video.id}>
                <Item onClick={() => handleSelect(video.videoUrl)}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 1,
                      mb: 1.5,
                      color: "#545454",
                    }}
                  >
                    <MovieIcon />
                    <Typography variant="subtitle2" fontWeight={700}>
                      Video
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      position: "relative",
                      borderRadius: 1.5,
                      overflow: "hidden",
                      mb: 1.5,
                      backgroundColor: "#111827",
                    }}
                  >
                    <video
                      muted
                      playsInline
                      preload="metadata"
                      poster={resolveAssetUrl(video.videoThumb)}
                      style={{ width: "100%", display: "block", aspectRatio: "16 / 9", objectFit: "cover" }}
                      src={resolveAssetUrl(video.videoUrl)}
                    />
                    <Box
                      sx={{
                        position: "absolute",
                        inset: 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        background: "linear-gradient(180deg, rgba(0,0,0,0.08) 0%, rgba(0,0,0,0.28) 100%)",
                      }}
                    >
                      <Button
                        variant="contained"
                        size="small"
                        onClick={(event) => {
                          event.stopPropagation();
                          handleSelect(video.videoUrl);
                        }}
                      >
                        Seç
                      </Button>
                    </Box>
                  </Box>

                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                      overflowWrap: "anywhere",
                    }}
                  >
                    {getDisplayName(video)}
                  </Typography>
                </Item>
              </Grid>
            ))}
          </Grid>
        )}
      </div>
    </Modal>
  );
}
